import React, { useEffect, useRef, useState } from 'react';
import './ContractWizard.css';
import { getContractApiUrl } from '../utils/apiUrls';
import PhoneNumberInput from './PhoneNumberInput';
import { firstIncompleteStepIndex } from '../utils/formSessionApi';
import { PA_QUESTION_SKIP_IF } from '../utils/paWizardDefaults';
import {
  formatRequiredFieldReason,
  wizardBlockedReason,
  WIZARD_ANSWER_QUESTION_REASON,
  WIZARD_PRIMARY_BUTTON_CLASS,
} from '../utils/wizardContinue';
import WizardProgress from './WizardProgress';
import WizardStepReady from './WizardStepReady';
import { isRealEstateAgentUserType } from '../constants/userTypes';

export const FIELD_STYLE = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #E8D5C4',
  borderRadius: '4px',
  fontSize: '0.9rem',
  background: 'white',
  boxSizing: 'border-box',
};

export const LABEL_STYLE = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: '4px',
};

/** Oklahoma buyer's agreement standard duration (days). */
export const BA_EXPIRATION_DAYS = 60;

/** Oklahoma BBSA §6/§7 — days after termination compensation still due (form default). */
export const BA_POST_TERMINATION_COMPENSATION_DAYS = 60;

/** Default commission decimal for Oklahoma (3%). */
export const OK_DEFAULT_COMMISSION = '0.03';

const STEP_COUNT = 8;
const MAX_BUYERS = 3;

export const BA_STEP_INDEX = {
  buyers: 0,
  commission_structure: 1,
  commission_percent: 2,
  commission_amount: 2,
  retainer_tc: 3,
  additional_provisions: 4,
  expiration_date: 5,
  post_termination_compensation_days: 6,
  agreement_date: 7,
};

export function buyerAgreementSkipValues(form = {}, parties = []) {
  const firstBuyer = (parties || []).find((party) => isBuyerRole(party.role)) || {};
  return {
    ...form,
    buyer_name: firstBuyer.name || form.buyer_name || '',
    buyer_email: firstBuyer.email || form.buyer_email || '',
  };
}

function resolveBuyerAgreementStartStep(wizardSteps, form, parties, initialValues = {}) {
  if (!hasAuthoritativeBuyerAgent(initialValues)) return 0;
  if (!wizardSteps?.length) return 0;
  const values = buyerAgreementSkipValues(form, parties);
  const stepIdx = firstIncompleteStepIndex(wizardSteps, values);
  const stepId = wizardSteps[stepIdx]?.step_id;
  let start = BA_STEP_INDEX[stepId] ?? stepIdx;
  const skipAmount = PA_QUESTION_SKIP_IF.commission_amount(values);
  const skipPercent = PA_QUESTION_SKIP_IF.commission_percent(values);
  if (stepId === 'commission_amount' && skipAmount) start = 3;
  if (stepId === 'commission_percent' && skipPercent) start = 3;
  return Math.min(Math.max(start, 0), STEP_COUNT - 1);
}

export const localToday = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toMMDDYYYY = (isoStr) => {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('-');
  return `${m}/${d}/${y}`;
};

export const defaultCommissionPercent = (licensedState) => (
  (licensedState || 'OK').toUpperCase() === 'OK' ? OK_DEFAULT_COMMISSION : ''
);

const CONTRACT_API_URL = getContractApiUrl();

export function agentPrefillFromUserProfile(profile) {
  if (!profile || profile.exists === false) {
    return {};
  }
  const state = profile.licensed_state || 'OK';
  if (!isRealEstateAgentUserType(profile.user_type)) {
    return {
      state,
      buyers_agent_commission_percent: defaultCommissionPercent(state),
    };
  }
  const brokerage = profile.brokerage_company_name || profile.company_name || '';
  const licenseNumber = profile.license_number || '';
  const companyAddress = profile.company_address || '';
  return {
    buyers_agent_name: profile.name || '',
    buyers_agent_email: profile.email || '',
    buyers_agent_phone: profile.phone_number || '',
    buyers_agent_brokerage: brokerage,
    buyers_agent_license_number: licenseNumber,
    buyers_agent_company_address: companyAddress,
    agent_name: profile.name || '',
    agent_email: profile.email || '',
    agent_phone: profile.phone_number || '',
    agent_brokerage: brokerage,
    license_number: licenseNumber,
    company_address: companyAddress,
    state,
    buyers_agent_commission_percent: defaultCommissionPercent(state),
  };
}

export function mergeBuyerAgreementInitialValues(...sources) {
  return Object.assign({}, ...sources.filter(Boolean));
}

export async function fetchAgentPrefillForEmail(email) {
  if (!email) return {};
  const res = await fetch(
    `${CONTRACT_API_URL}/user/profile?email=${encodeURIComponent(email)}`,
  );
  if (!res.ok) return {};
  const profile = await res.json();
  return agentPrefillFromUserProfile(profile);
}

export function useBuyerAgreementAgentPrefill(userEmail, extraInitialValues = null) {
  const extraRef = useRef(extraInitialValues);
  extraRef.current = extraInitialValues;

  const [initialValues, setInitialValues] = useState(() => (
    mergeBuyerAgreementInitialValues(extraInitialValues)
  ));
  const [licensedState, setLicensedState] = useState(
    extraInitialValues?.state || extraInitialValues?.licensed_state || 'OK',
  );
  const [ready, setReady] = useState(!userEmail);

  useEffect(() => {
    if (!userEmail) {
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    setReady(false);
    (async () => {
      try {
        const agentPrefill = await fetchAgentPrefillForEmail(userEmail);
        if (cancelled) return;
        setInitialValues(mergeBuyerAgreementInitialValues(agentPrefill, extraRef.current));
        setLicensedState(agentPrefill.state || 'OK');
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userEmail]);

  return { initialValues, licensedState, ready };
}

const defaultParties = () => ([
  { role: 'buyer', name: '', email: '', phone: '' },
]);

export function buyerAgentFieldsFromValues(values = {}) {
  return {
    name: values.buyers_agent_name || values.agent_name || '',
    email: values.buyers_agent_email || values.agent_email || '',
    phone: values.buyers_agent_phone || values.agent_phone || '',
    company: values.buyers_agent_brokerage || values.agent_brokerage || '',
    license_number: values.buyers_agent_license_number || values.license_number || '',
    company_address: values.buyers_agent_company_address || values.company_address || '',
  };
}

export function hasAuthoritativeBuyerAgent(values = {}) {
  const fields = buyerAgentFieldsFromValues(values);
  return Boolean((fields.name || '').trim() && (fields.email || '').trim());
}

export function buildAgentPartyFromFields(fields = {}) {
  const name = (fields.name || '').trim();
  const email = (fields.email || '').trim();
  if (!name || !email) return null;
  return {
    role: 'buyers_agent',
    name,
    email,
    phone: fields.phone || '',
    company: fields.company || '',
    license_number: fields.license_number || '',
    company_address: fields.company_address || '',
  };
}

export function buildAgentPartyFromInitialValues(initialValues = {}) {
  return buildAgentPartyFromFields(buyerAgentFieldsFromValues(initialValues));
}

export function buildPartiesFromInitialValues(initialValues = {}) {
  if (initialValues.buyer_names && Array.isArray(initialValues.buyer_names)) {
    return initialValues.buyer_names.map((name, i) => ({
      role: i === 0 ? 'buyer' : 'co_buyer',
      name,
      email: '',
      phone: '',
    }));
  }
  return defaultParties();
}

export function buildInitialFormState(initialValues = {}, licensedState = 'OK') {
  return {
    agreement_date: initialValues.agreement_date || '',
    expiration_date: initialValues.expiration_date || inDays(BA_EXPIRATION_DAYS),
    commission_structure: initialValues.commission_structure
      || (initialValues.buyers_agent_commission_amount && !initialValues.buyers_agent_commission_percent
        ? 'fixed'
        : 'percent'),
    buyers_agent_commission_percent:
      initialValues.buyers_agent_commission_percent
      ?? (initialValues.commission_structure === 'fixed' ? '' : defaultCommissionPercent(licensedState)),
    buyers_agent_commission_amount: initialValues.buyers_agent_commission_amount || '',
    buyers_agent_commission_payer: initialValues.buyers_agent_commission_payer || 'seller',
    retainer_fee_amount: initialValues.retainer_fee_amount || '',
    buyers_tc_fee_amount: initialValues.buyers_tc_fee_amount || '',
    additional_provisions: initialValues.additional_provisions || '',
    post_termination_compensation_days:
      initialValues.post_termination_compensation_days ?? BA_POST_TERMINATION_COMPENSATION_DAYS,
  };
}

function isBuyerRole(role) {
  return role === 'buyer' || role === 'co_buyer';
}

const STEP_QUESTIONS = (licensedState, defaultExpiration, today, commissionStructure) => [
  'Who is the Buyer? Add co-buyers if needed.',
  'Standard % commission structure or fixed fee?',
  commissionStructure === 'fixed'
    ? "What is the buyer's agent fixed commission fee?"
    : `What is the buyer's agent commission? The Oklahoma standard is 3% (${OK_DEFAULT_COMMISSION}).`,
  'Are there any other fees, such as a retainer or transaction coordinator (TC) fee? Leave blank if none.',
  'Any additional provisions for Section 15? (Optional)',
  `When does this buyer agreement expire? The standard in Oklahoma is ${BA_EXPIRATION_DAYS} days from today (${toMMDDYYYY(defaultExpiration)}).`,
  'How long after this agreement ends is compensation for a new purchase agreement still due?',
  `Do you want today (${toMMDDYYYY(today)}) to be the agreement date?`,
];

function mergeFormState(prev, next) {
  const out = { ...prev };
  Object.entries(next).forEach(([key, value]) => {
    const current = out[key];
    const empty = current === undefined || current === null || String(current).trim() === '';
    const incoming = value !== undefined && value !== null && String(value).trim() !== '';
    if (empty && incoming) {
      out[key] = value;
    }
  });
  return out;
}

function mergeParties(prev, next) {
  if (!Array.isArray(next) || next.length === 0) return prev;
  if (!Array.isArray(prev) || prev.length === 0) return next;
  return next.map((party, idx) => {
    const existing = prev[idx] || {};
    return {
      ...existing,
      ...party,
      role: party.role || existing.role,
      name: existing.name?.trim() ? existing.name : party.name,
      email: existing.email?.trim() ? existing.email : party.email,
      phone: existing.phone?.trim() ? existing.phone : party.phone,
      company: existing.company?.trim() ? existing.company : party.company,
    };
  });
}

const BuyerAgreementForm = ({
  initialValues = {},
  licensedState = 'OK',
  onSubmit,
  saving = false,
  error = null,
  submitLabel = 'Create Buyer Agreement',
  onCancel,
  cancelLabel = 'Cancel',
  stickyFooter = false,
  stackedLayout = false,
  wizardSteps = [],
  onBackToCatalog,
  valuesReady = true,
}) => {
  const [currentStep, setCurrentStep] = useState(() => resolveBuyerAgreementStartStep(
    wizardSteps,
    buildInitialFormState(initialValues, licensedState),
    buildPartiesFromInitialValues(initialValues),
    initialValues,
  ));
  const [form, setForm] = useState(() => buildInitialFormState(initialValues, licensedState));
  const [parties, setParties] = useState(() => buildPartiesFromInitialValues(initialValues));
  const [agentDraft, setAgentDraft] = useState(() => buyerAgentFieldsFromValues(initialValues));
  const initialAppliedRef = useRef(false);
  const collectBuyerAgent = !hasAuthoritativeBuyerAgent(initialValues);

  useEffect(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) return;
    const nextForm = buildInitialFormState(initialValues, licensedState);
    const nextParties = buildPartiesFromInitialValues(initialValues);
    const nextAgent = buyerAgentFieldsFromValues(initialValues);
    if (!initialAppliedRef.current) {
      setForm(nextForm);
      setParties(nextParties);
      setAgentDraft(nextAgent);
      initialAppliedRef.current = true;
      return;
    }
    setForm((prev) => mergeFormState(prev, nextForm));
    setParties((prev) => mergeParties(prev, nextParties));
    setAgentDraft((prev) => ({
      name: prev.name?.trim() ? prev.name : nextAgent.name,
      email: prev.email?.trim() ? prev.email : nextAgent.email,
      phone: prev.phone?.trim() ? prev.phone : nextAgent.phone,
      company: prev.company?.trim() ? prev.company : nextAgent.company,
      license_number: prev.license_number?.trim() ? prev.license_number : nextAgent.license_number,
      company_address: prev.company_address?.trim() ? prev.company_address : nextAgent.company_address,
    }));
  }, [initialValues, licensedState]);

  const updateAgentDraft = (field, value) => setAgentDraft((prev) => ({ ...prev, [field]: value }));

  const updateForm = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const updateParty = (idx, field, value) => {
    setParties((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const addCoBuyer = () => {
    setParties((prev) => {
      const buyerCount = prev.filter((p) => isBuyerRole(p.role)).length;
      if (buyerCount >= MAX_BUYERS) return prev;
      return [...prev, { role: 'co_buyer', name: '', email: '', phone: '' }];
    });
  };

  const removePartyAt = (idx) => {
    setParties((prev) => prev.filter((_, i) => i !== idx));
  };

  const defaultExpiration = inDays(BA_EXPIRATION_DAYS);
  const today = localToday();
  const defaultCommission = defaultCommissionPercent(licensedState);

  const buyerPartyEntries = parties
    .map((party, idx) => ({ party, idx }))
    .filter(({ party }) => isBuyerRole(party.role));

  const canProceed = () => {
    switch (currentStep) {
      case 0: {
        const hasBuyer = buyerPartyEntries.some(({ party }) => (
          (party.name || '').trim().length > 0 && (party.email || '').trim().length > 0
        ));
        if (!hasBuyer) return false;
        if (!collectBuyerAgent) return true;
        return Boolean((agentDraft.name || '').trim() && (agentDraft.email || '').trim());
      }
      case 1:
        return form.commission_structure === 'percent' || form.commission_structure === 'fixed';
      case 2:
        return form.commission_structure === 'fixed'
          ? parseFloat(form.buyers_agent_commission_amount) > 0
          : (form.buyers_agent_commission_percent || '').toString().trim().length > 0;
      case 3:
        return true;
      case 4:
        return true;
      case 5:
        return (form.expiration_date || '').length > 0;
      case 6:
        return Number(form.post_termination_compensation_days) > 0;
      case 7:
        return (form.agreement_date || '').length > 0;
      default:
        return false;
    }
  };

  const continueBlockedReason = () => {
    if (canProceed()) return '';
    switch (currentStep) {
      case 0:
        return formatRequiredFieldReason(
          collectBuyerAgent
            ? ['a buyer name', 'email', "the buyer's agent name", 'email']
            : ['a buyer name', 'email'],
        );
      case 1:
        return formatRequiredFieldReason(['a commission structure'], { verb: 'Select' });
      case 2:
        return formatRequiredFieldReason([
          form.commission_structure === 'fixed' ? 'the fixed commission fee' : 'the commission percentage',
        ]);
      case 5:
        return formatRequiredFieldReason(['the expiration date']);
      case 6:
        return formatRequiredFieldReason(['the post-termination days']);
      case 7:
        return formatRequiredFieldReason(['the agreement date']);
      default:
        return wizardBlockedReason({ ready: false, fallback: WIZARD_ANSWER_QUESTION_REASON });
    }
  };

  const handleNext = () => {
    if (!canProceed()) return;
    if (currentStep < STEP_COUNT - 1) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 0 && onBackToCatalog) {
      onBackToCatalog();
      return;
    }
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep < STEP_COUNT - 1) {
      handleNext();
      return;
    }
    if (!canProceed() || saving) return;
    const buyerParties = parties.filter((p) => isBuyerRole(p.role) && (p.name || p.email));
    const agentParty = collectBuyerAgent
      ? buildAgentPartyFromFields(agentDraft)
      : buildAgentPartyFromInitialValues(initialValues);
    if (!agentParty) return;
    const allParties = [...buyerParties, agentParty];
    const useFixed = form.commission_structure === 'fixed';
    await onSubmit({
      ...form,
      commission_structure: useFixed ? 'fixed' : 'percent',
      buyers_agent_commission_percent: useFixed ? null : form.buyers_agent_commission_percent,
      buyers_agent_commission_amount: useFixed ? form.buyers_agent_commission_amount : null,
      post_termination_compensation_days: Number(form.post_termination_compensation_days) || BA_POST_TERMINATION_COMPENSATION_DAYS,
      street_address:
        initialValues.street_address
        || initialValues.company_address
        || initialValues.buyers_agent_company_address
        || '',
      city: initialValues.city || '',
      state: initialValues.state || licensedState,
      zip_code: initialValues.zip_code || '',
      parties: allParties,
    });
  };

  const partyBlockClass = stackedLayout ? 'ba-party-block ba-party-block--stacked' : 'ba-party-block';

  const renderBuyersStep = () => (
    <div className={partyBlockClass}>
      {buyerPartyEntries.map(({ party, idx }, displayIdx) => (
        <div key={`buyer-${idx}`} className="ba-party-card">
          <div className="ba-party-card-header">
            <span className="ba-party-card-title">
              {displayIdx === 0 ? 'Buyer' : `Co-Buyer ${displayIdx}`}
            </span>
            {displayIdx > 0 && (
              <button type="button" className="ba-party-remove" onClick={() => removePartyAt(idx)}>
                Remove
              </button>
            )}
          </div>
          <label style={LABEL_STYLE}>Name</label>
          <input
            className="wizard-input"
            style={FIELD_STYLE}
            placeholder="Full name"
            value={party.name}
            onChange={(e) => updateParty(idx, 'name', e.target.value)}
          />
          <label style={{ ...LABEL_STYLE, marginTop: 12 }}>Email</label>
          <input
            type="email"
            className="wizard-input"
            style={FIELD_STYLE}
            placeholder="email@example.com"
            value={party.email}
            onChange={(e) => updateParty(idx, 'email', e.target.value)}
          />
          <label style={{ ...LABEL_STYLE, marginTop: 12 }}>Phone</label>
          <PhoneNumberInput
            className="wizard-input"
            style={FIELD_STYLE}
            placeholder="e.g. 405-555-1234"
            value={party.phone}
            onChange={(e) => updateParty(idx, 'phone', e.target.value)}
          />
        </div>
      ))}
      {buyerPartyEntries.length < MAX_BUYERS && (
        <button type="button" className="wizard-button secondary ba-add-person" onClick={addCoBuyer}>
          + Add Co-Buyer
        </button>
      )}
      {collectBuyerAgent && (
        <div className="ba-party-card">
          <div className="ba-party-card-header">
            <span className="ba-party-card-title">Buyer&apos;s Agent</span>
          </div>
          <label style={LABEL_STYLE}>Name</label>
          <input
            className="wizard-input"
            style={FIELD_STYLE}
            placeholder="Full name"
            value={agentDraft.name}
            onChange={(e) => updateAgentDraft('name', e.target.value)}
          />
          <label style={{ ...LABEL_STYLE, marginTop: 12 }}>Email</label>
          <input
            type="email"
            className="wizard-input"
            style={FIELD_STYLE}
            placeholder="email@example.com"
            value={agentDraft.email}
            onChange={(e) => updateAgentDraft('email', e.target.value)}
          />
          <label style={{ ...LABEL_STYLE, marginTop: 12 }}>Phone</label>
          <PhoneNumberInput
            className="wizard-input"
            style={FIELD_STYLE}
            placeholder="e.g. 405-555-1234"
            value={agentDraft.phone}
            onChange={(e) => updateAgentDraft('phone', e.target.value)}
          />
          <label style={{ ...LABEL_STYLE, marginTop: 12 }}>Brokerage</label>
          <input
            className="wizard-input"
            style={FIELD_STYLE}
            placeholder="Brokerage name"
            value={agentDraft.company}
            onChange={(e) => updateAgentDraft('company', e.target.value)}
          />
        </div>
      )}
    </div>
  );

  const renderCommissionStructureStep = () => (
    <div className="wizard-earnest-money">
      {[
        { value: 'percent', label: 'Standard %' },
        { value: 'fixed', label: 'Fixed fee' },
      ].map((choice) => (
        <button
          key={choice.value}
          type="button"
          className={`wizard-button ${form.commission_structure === choice.value ? 'active' : ''}`}
          onClick={() => {
            setForm((prev) => ({
              ...prev,
              commission_structure: choice.value,
              buyers_agent_commission_percent: choice.value === 'fixed' ? '' : (prev.buyers_agent_commission_percent || defaultCommission),
              buyers_agent_commission_amount: choice.value === 'percent' ? '' : prev.buyers_agent_commission_amount,
            }));
          }}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );

  const renderCommissionStep = () => (
    <>
      {form.commission_structure === 'fixed' ? (
        <div className="wizard-input-group">
          <input
            type="number"
            step="0.01"
            min="0"
            className="wizard-input"
            placeholder="e.g. 4500"
            value={form.buyers_agent_commission_amount}
            onChange={(e) => updateForm('buyers_agent_commission_amount', e.target.value)}
          />
        </div>
      ) : (
        <div className="wizard-earnest-money">
          <button
            type="button"
            className={`wizard-button ${form.buyers_agent_commission_percent === defaultCommission ? 'active' : ''}`}
            onClick={() => updateForm('buyers_agent_commission_percent', defaultCommission)}
          >
            3%
          </button>
          <div className="wizard-input-group">
            <input
              type="number"
              step="0.001"
              min="0"
              max="1"
              className="wizard-input"
              placeholder="e.g. 0.03"
              value={form.buyers_agent_commission_percent}
              onChange={(e) => updateForm('buyers_agent_commission_percent', e.target.value)}
            />
          </div>
        </div>
      )}
      <label style={{ ...LABEL_STYLE, marginTop: 16, display: 'block' }}>Commission paid by</label>
      <select
        className="wizard-input"
        style={{ ...FIELD_STYLE, marginTop: 4 }}
        value={form.buyers_agent_commission_payer}
        onChange={(e) => updateForm('buyers_agent_commission_payer', e.target.value)}
      >
        <option value="seller">Seller</option>
        <option value="buyer">Buyer</option>
        <option value="split">Split</option>
      </select>
    </>
  );

  const renderFeesStep = () => (
    <div className="ba-fees-step">
      <label style={LABEL_STYLE}>Retainer Fee ($)</label>
      <input
        type="number"
        min="0"
        step="0.01"
        className="wizard-input"
        style={FIELD_STYLE}
        placeholder="Leave blank if none"
        value={form.retainer_fee_amount}
        onChange={(e) => updateForm('retainer_fee_amount', e.target.value)}
      />
      <label style={{ ...LABEL_STYLE, marginTop: 16 }}>TC Fee ($)</label>
      <input
        type="number"
        min="0"
        step="0.01"
        className="wizard-input"
        style={FIELD_STYLE}
        placeholder="Leave blank if none"
        value={form.buyers_tc_fee_amount}
        onChange={(e) => updateForm('buyers_tc_fee_amount', e.target.value)}
      />
    </div>
  );

  const renderProvisionsStep = () => (
    <div className="ba-provisions-step">
      <textarea
        className="wizard-input ba-provisions-textarea"
        style={{ ...FIELD_STYLE, minHeight: 160, resize: 'vertical' }}
        placeholder="Enter any additional provisions to appear in Section 15 of the agreement (optional)."
        value={form.additional_provisions}
        onChange={(e) => updateForm('additional_provisions', e.target.value)}
      />
    </div>
  );

  const renderExpirationStep = () => (
    <div className="wizard-signing-date wizard-ba-expiration">
      <button
        type="button"
        className={`wizard-button ${form.expiration_date === defaultExpiration ? 'active' : ''}`}
        onClick={() => updateForm('expiration_date', defaultExpiration)}
      >
        {BA_EXPIRATION_DAYS} days ({toMMDDYYYY(defaultExpiration)})
      </button>
      <div className="wizard-input-group">
        <input
          type="date"
          className="wizard-input"
          value={form.expiration_date}
          min={today}
          onChange={(e) => updateForm('expiration_date', e.target.value)}
          aria-label="Custom expiration date"
        />
      </div>
    </div>
  );

  const renderPostTerminationStep = () => (
    <div className="wizard-signing-date wizard-ba-expiration">
      <button
        type="button"
        className={`wizard-button ${Number(form.post_termination_compensation_days) === BA_POST_TERMINATION_COMPENSATION_DAYS ? 'active' : ''}`}
        onClick={() => updateForm('post_termination_compensation_days', BA_POST_TERMINATION_COMPENSATION_DAYS)}
      >
        {BA_POST_TERMINATION_COMPENSATION_DAYS} days
      </button>
      <div className="wizard-input-group">
        <input
          type="number"
          min="1"
          step="1"
          className="wizard-input"
          placeholder="Custom number of days"
          value={form.post_termination_compensation_days}
          onChange={(e) => updateForm('post_termination_compensation_days', e.target.value)}
          aria-label="Days after agreement ends compensation is still due"
        />
      </div>
    </div>
  );

  const renderAgreementDateStep = () => (
    <div className="wizard-signing-date">
      <button
        type="button"
        className={`wizard-button ${form.agreement_date === today ? 'active' : ''}`}
        onClick={() => updateForm('agreement_date', today)}
      >
        Yes — use today ({toMMDDYYYY(today)})
      </button>
      <div className="wizard-input-group">
        <input
          type="date"
          className="wizard-input"
          value={form.agreement_date}
          onChange={(e) => updateForm('agreement_date', e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          aria-label="Custom agreement date"
        />
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderBuyersStep();
      case 1: return renderCommissionStructureStep();
      case 2: return renderCommissionStep();
      case 3: return renderFeesStep();
      case 4: return renderProvisionsStep();
      case 5: return renderExpirationStep();
      case 6: return renderPostTerminationStep();
      case 7: return renderAgreementDateStep();
      default: return null;
    }
  };

  const isLastStep = currentStep === STEP_COUNT - 1;

  const actionButtons = (
    <>
      <button
        type="button"
        className="wizard-button secondary"
        onClick={handlePrevious}
        disabled={currentStep === 0 && !onBackToCatalog}
      >
        Previous
      </button>
      {isLastStep ? (
        <button
          type="button"
          className={WIZARD_PRIMARY_BUTTON_CLASS}
          disabled={!canProceed() || saving}
          onClick={handleSubmit}
        >
          {saving ? 'Creating…' : submitLabel}
        </button>
      ) : (
        <button
          type="button"
          className={WIZARD_PRIMARY_BUTTON_CLASS}
          onClick={handleNext}
          disabled={!canProceed()}
        >
          Next
        </button>
      )}
      {onCancel && currentStep === 0 && (
        <button type="button" className="wizard-button secondary" onClick={onCancel}>
          {cancelLabel}
        </button>
      )}
    </>
  );

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
        }
      }}
      className={`ba-wizard-form${stackedLayout ? ' ba-wizard-form--stacked' : ''}`}
    >
      <WizardProgress current={currentStep + 1} total={STEP_COUNT} />

      <div className="wizard-content">
        <WizardStepReady ready={valuesReady}>
          <div className="wizard-question">
            <h3>
              {currentStep === 0 && collectBuyerAgent
                ? "Who is the Buyer? Add the buyer's agent and any co-buyers."
                : STEP_QUESTIONS(licensedState, defaultExpiration, today, form.commission_structure)[currentStep]}
            </h3>
            {renderStepContent()}
          </div>

          {error && (
            <div style={{ color: '#dc2626', marginBottom: 12, fontSize: '0.875rem' }}>{error}</div>
          )}

          {!stickyFooter && (
            <div className="wizard-actions">{actionButtons}</div>
          )}
        </WizardStepReady>
      </div>
      {stickyFooter && valuesReady && (
        <div className="wizard-sticky-footer">
          {actionButtons}
        </div>
      )}
    </form>
  );
};

export default BuyerAgreementForm;
